import { useEffect } from "react";
import { get } from "utils/request";
import { toast } from "react-toastify";
import clsx from "clsx";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useLoaderContext } from "components/context/LoaderContext";
import { useBrandContext } from "components/context/BrandContext";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import BrandDetailsForm from "../../components/organisms/BrandDetailsForm";
import Button from "../../components/atoms/buttons/Button";
import BrandAddressForm from "../../components/organisms/BrandAddressForm";
import Loading from "../../components/atoms/Loading";
import FormHeadingWithIcon from "../../components/atoms/forms/FormHeadingWithIcon";
import ImageEditElement from "../../components/molecules/ImageEditElement";
import SocialLinkElement from "../../components/molecules/forms/SocialLinkElement";
import { BrandOnboardingStepsCopy } from "../../constants/constants";
import { TOAST_CONFIG } from "../../constants";

function EditBrandProfile({ loading, handleFormSubmit, register, errors, getValues, setValue, watch }: any) {
  const iconDetails = BrandOnboardingStepsCopy[0].settingIcon;
  const iconAddress = BrandOnboardingStepsCopy[1].settingIcon;
  const iconLinks = BrandOnboardingStepsCopy[2].settingIcon;
  const { brandId, brandDetails } = useBrandContext();
  const { setLoading } = useLoaderContext();

  const profileImage = watch("profile_image") && watch("profile_image")[0];
  const coverImage = watch("cover_image") && watch("cover_image")[0];
  const profileImageURL = watch("profile_image_url");
  const coverImageURL = watch("cover_image_url");

  const handleSetImageValue = (file: FileList, id: string) => {
    setValue(id, file, {
      shouldValidate: true,
    });
  };
  const handleClearProfileImageValue = () => {
    setValue("profile_image", undefined, {
      shouldValidate: true,
    });
  };
  const handleClearCoverImageValue = () => {
    setValue("cover_image", undefined, {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (brandDetails.is_stripe_connected && window.location.href.includes("status=success")) {
      toast.success("Congratulations! Your Stripe account is now connected.", TOAST_CONFIG);
    } else if (!brandDetails.is_stripe_connected && window.location.href.includes("status=failure")) {
      toast.error(
        "We're sorry, but there was an issue connecting your Stripe account. If you continue to experience difficulties, don't hesitate to contact us for assistance. We're here to help you get set up and running smoothly!",
        TOAST_CONFIG,
      );
    }
  }, []);

  return (
    <div className="max-w-5xl pb-7">
      {loading && <Loading />}

      <div className="mt-6 flex justify-between pr-44">
        <FormHeadingWithIcon iconAdditionalClasses="w-6 h-6" icon={iconDetails} heading="Brand Info" />
        <Button
          icon={brandDetails.is_stripe_connected && <CheckCircleIcon className="h-5 w-5" />}
          text={brandDetails.is_stripe_connected ? "STRIPE CONNECTED" : "CONNECT STRIPE"}
          variant="magnetiqSolid"
          disabled={brandDetails.is_stripe_connected}
          additionalClasses={clsx(
            "!text-base !px-4 !py-1 !text-white",
            brandDetails.is_stripe_connected ? "!bg-lime-700 disabled:!opacity-90" : "!bg-[#635bff]",
          )}
          onClick={() => {
            setLoading(true, "Please Wait! Taking you to stripe....");
            get(
              `/api/v1/payments/stripe/connect?brand_id=${brandId}&redirect_to=${encodeURIComponent(
                window.location.href,
              )}`,
              {
                processData: ({ data }) => {
                  window.location.assign(data.redirect_url);
                },
                processError: () => {
                  setLoading(false);
                  toast.error("Unexpected Error, Please try Again!", TOAST_CONFIG);
                },
              },
            );
          }}
        />
      </div>
      <div className="mt-7 grid w-full grid-cols-12 items-start gap-x-4 ">
        <div className="col-span-8 lg:col-span-4  xl:col-span-3">
          <ImageEditElement
            additionalClasses="w-full"
            label="Profile Image"
            id="profile_image"
            setFieldValue={handleSetImageValue}
            image={profileImage}
            imageURL={profileImageURL}
            handleClearImageButton={handleClearProfileImageValue}
          />
          <ErrorMessage errorMessage={errors.profile_image?.message} />
        </div>
        <div className="col-span-12 lg:col-span-8 xl:col-span-7">
          <ImageEditElement
            label="Cover Image"
            id="cover_image"
            setFieldValue={handleSetImageValue}
            image={coverImage}
            imageURL={coverImageURL}
            handleClearImageButton={handleClearCoverImageValue}
          />
          <ErrorMessage errorMessage={errors.cover_image?.message} />
        </div>
      </div>

      {/* Brand About Form  */}
      <div className="mt-6 w-3/4">
        <BrandDetailsForm isEditForm gapY={2} />
      </div>

      {/* Address Form */}
      <hr className="mb-8 mt-3 border-dashed border-white" />
      <FormHeadingWithIcon icon={iconAddress} heading="Address Details" />
      <BrandAddressForm />

      <hr className="mb-8 mt-3 border-dashed border-white" />

      <FormHeadingWithIcon icon={iconLinks} heading="Links" />
      <div className="mt-5 grid grid-cols-10 gap-y-2 md:gap-x-12 md:gap-y-4">
        <div className="col-span-10  md:col-span-5">
          <SocialLinkElement
            id="website_link"
            label="Website"
            register={register}
            getValues={getValues}
            setValue={setValue}
            errorMessage={errors.website_link?.message}
          />
        </div>
        <div className=" col-span-10 md:col-span-5">
          <SocialLinkElement
            id="instagram_link"
            label="Instagram"
            register={register}
            placeHolder="Enter Instagram Link"
            getValues={getValues}
            setValue={setValue}
            errorMessage={errors.instagram_link?.message}
          />
        </div>
        <div className="col-span-10 md:col-span-5">
          <SocialLinkElement
            id="twitter_link"
            label="Twitter"
            register={register}
            placeHolder="Enter Twitter Link"
            getValues={getValues}
            setValue={setValue}
            errorMessage={errors.twitter_link?.message}
          />
        </div>
        <div className="col-span-10 md:col-span-5">
          <SocialLinkElement
            id="other_link"
            label="Other Links"
            register={register}
            placeHolder="Enter Other Link"
            getValues={getValues}
            setValue={setValue}
            errorMessage={errors.other_link?.message}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-center md:justify-end ">
        <Button
          text="SAVE"
          onClick={handleFormSubmit}
          additionalClasses="xl:text-xs
          2xl:text-sm xl:px-8 xl:py-3 2xl:px-10 2xl:py-4"
          variant="rustPinkSolid"
        />
      </div>
    </div>
  );
}

export default EditBrandProfile;
